import Swal from 'sweetalert2';

/**
 * Centralized SweetAlert2 utility for consistent alerts across the app
 * Styled to match the emerald theme (#059669)
 */

// Default configuration matching app theme
const defaultConfig = {
  confirmButtonColor: '#059669', // Emerald theme color
  cancelButtonColor: '#6B7280', // Gray for cancel
  showClass: {
    popup: 'animate__animated animate__fadeIn animate__faster'
  },
  hideClass: {
    popup: 'animate__animated animate__fadeOut animate__faster'
  }
};

/**
 * Show success alert
 * @param {string} title - Alert title
 * @param {string} text - Alert message (optional)
 * @param {object} options - Additional SweetAlert2 options
 */
export const showSuccess = (title, text = '', options = {}) => {
  return Swal.fire({
    icon: 'success',
    title,
    text,
    confirmButtonColor: '#059669',
    confirmButtonText: 'OK',
    timer: 3000,
    timerProgressBar: true,
    ...options
  });
};

/**
 * Show error alert
 * @param {string} title - Alert title
 * @param {string} text - Alert message (optional)
 * @param {object} options - Additional SweetAlert2 options
 */
export const showError = (title, text = '', options = {}) => {
  return Swal.fire({
    icon: 'error',
    title,
    text,
    confirmButtonColor: '#DC2626', // Red for errors
    confirmButtonText: 'OK',
    ...options
  });
};

/**
 * Show warning alert
 * @param {string} title - Alert title
 * @param {string} text - Alert message (optional)
 * @param {object} options - Additional SweetAlert2 options
 */
export const showWarning = (title, text = '', options = {}) => {
  return Swal.fire({
    icon: 'warning',
    title,
    text,
    confirmButtonColor: '#F59E0B', // Amber for warnings
    confirmButtonText: 'OK',
    ...options
  });
};

/**
 * Show info alert
 * @param {string} title - Alert title
 * @param {string} text - Alert message (optional)
 * @param {object} options - Additional SweetAlert2 options
 */
export const showInfo = (title, text = '', options = {}) => {
  return Swal.fire({
    icon: 'info',
    title,
    text,
    confirmButtonColor: '#3B82F6', // Blue for info
    confirmButtonText: 'OK',
    ...options
  });
};

/**
 * Show confirmation dialog
 * @param {string} title - Confirmation title
 * @param {string} text - Confirmation message
 * @param {object} options - Additional options
 * @returns {Promise<boolean>} - Returns true if confirmed, false if cancelled
 */
export const showConfirm = (title, text = '', options = {}) => {
  return Swal.fire({
    icon: 'question',
    title,
    text,
    showCancelButton: true,
    confirmButtonColor: '#059669',
    cancelButtonColor: '#6B7280',
    confirmButtonText: options.confirmButtonText || 'Yes',
    cancelButtonText: options.cancelButtonText || 'Cancel',
    reverseButtons: true, // Cancel on left, Confirm on right
    ...options
  }).then((result) => {
    return result.isConfirmed;
  });
};

/**
 * Show delete confirmation dialog
 * @param {string} itemName - Name of item to delete
 * @param {object} options - Additional options
 * @returns {Promise<boolean>} - Returns true if confirmed, false if cancelled
 */
export const showDeleteConfirm = (itemName = 'this item', options = {}) => {
  return Swal.fire({
    icon: 'warning',
    title: 'Are you sure?',
    html: `You are about to delete <strong>${itemName}</strong>.<br/>This action cannot be undone.`,
    showCancelButton: true,
    confirmButtonColor: '#DC2626', // Red for delete
    cancelButtonColor: '#6B7280',
    confirmButtonText: 'Yes, delete it',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    ...options
  }).then((result) => {
    return result.isConfirmed;
  });
};

/**
 * Show loading alert
 * @param {string} title - Loading message
 */
export const showLoading = (title = 'Processing...') => {
  Swal.fire({
    title,
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      Swal.showLoading();
    }
  });
};

/**
 * Close any open alert
 */
export const closeAlert = () => {
  Swal.close();
};

/**
 * Show toast notification (bottom-right corner)
 * @param {string} message - Toast message
 * @param {string} icon - Icon type ('success', 'error', 'warning', 'info')
 * @param {number} timer - Auto-close timer in ms (default: 3000)
 */
export const showToast = (message, icon = 'success', timer = 3000) => {
  const Toast = Swal.mixin({
    toast: true,
    position: 'bottom-end',
    showConfirmButton: false,
    timer,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
  });

  return Toast.fire({
    icon,
    title: message
  });
};

/**
 * Show simple alert (replaces window.alert)
 * @param {string} message - Alert message
 */
export const showAlert = (message) => {
  return Swal.fire({
    text: message,
    confirmButtonColor: '#059669',
    confirmButtonText: 'OK'
  });
};

export default {
  showSuccess,
  showError,
  showWarning,
  showInfo,
  showConfirm,
  showDeleteConfirm,
  showLoading,
  closeAlert,
  showToast,
  showAlert
};
